import { relationshipOptions, roleOptions, temporaryLivingSituationOptions } from 'constants/enums/residenceOptions';
import { DEFAULT_LATEST, TODAY } from 'constants/dateLimits';

import address from 'forms/shared/validators/models/location/models/address';
import name from 'forms/shared/validators/models/name/name';
import phoneModelValidator from 'forms/shared/validators/models/phone/phone';
import physicalAddress from 'forms/shared/validators/models/physicalAddress/physicalAddress';
import { DateTime } from 'luxon';
import {
  today, dateWithinMonthYearSafeRange, setToStartOfMonth, getSafeDateTime, getApplicantRequiredDuration, getThresholdYears, isSafeDateLater
} from 'helpers/date';
import { isInternational, isPO } from 'helpers/location';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { REQUIRED } from 'constants/errors';
import { LAST_THREE_YEARS } from 'constants/dateLimits';
import * as formTypes from 'constants/formTypes';
import { SafeDateTime } from 'helpers/types';
import { FormDetails } from 'util/singletons';
import { isPVQForm } from 'helpers/form';
import { hasYesOrNo } from 'models/validate';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { DeepSafePartial } from 'types/types';
import { HistoryResidenceItemModel } from '../types';
import { isSafeUnitedStates } from 'helpers/locations';

const residenceRequiresReference = (
  dates: DeepSafePartial<HistoryResidenceItemModel['Dates']> = {}
) => {
  const { from, present } = dates;
  const to = present ? today.toObject() : dates.to;
  const eqipResidenceVerifierRequired = DateTime.fromJSDate(new Date(LAST_THREE_YEARS.year, LAST_THREE_YEARS.month - 1, 1, 0));
  const formDetails = FormDetails.getInstance().getData();

  if (to === undefined || from === undefined || isPVQForm(formDetails.formType)) { return false; }

  return (
    dateWithinMonthYearSafeRange(to, eqipResidenceVerifierRequired)
    || dateWithinMonthYearSafeRange(from, eqipResidenceVerifierRequired)
  );
};

const historyResidenceItemValidator: ValidateJSModel<HistoryResidenceItemModel> = {
  Dates: (value, attributes, attributeName, options) => {
    let { applicantBirthdate } = options;
    const thresholdYears = getThresholdYears(options?.formType);
    const applicantEighteenthBirthdate: SafeDateTime | undefined = options?.applicantBirthdate && setToStartOfMonth(getSafeDateTime(getSafeDateTime(options?.applicantBirthdate)?.datetime?.plus({ years: 18 })));

    const { coverage } = getApplicantRequiredDuration({
      requiredDuration: options?.requireYears ? { years: options?.requireYears } : undefined,
      applicantBirthdate,
      thresholdYears,
      type: 'residence'
    });

    const toDate = getSafeDateTime(value?.to);
    const requiredDate = setToStartOfMonth(getSafeDateTime(TODAY.minus(coverage)));
    let earliestDate;
    let earliestMessage;

    if (options?.formType && options?.formType === 'SF86' && applicantEighteenthBirthdate?.valid && requiredDate?.valid && isSafeDateLater(requiredDate, applicantEighteenthBirthdate)) {
      earliestDate = applicantEighteenthBirthdate;
      earliestMessage = 'error.beforeDuration';
    } else if (requiredDate?.valid) {
      earliestDate = requiredDate;
      earliestMessage = 'error.beforeDuration';
    }

    const beyondRequired = (toDate?.valid && earliestDate?.valid)
      ? isSafeDateLater(earliestDate, toDate) : false;

    if (isPVQForm(options?.formType) && applicantBirthdate) {
      // The day of the month for the residence "from" date is not requested.
      // Since the default value for the "from" day of the month is set to '1',
      // the day used for the birthdate comparison is likewise adjusted.
      applicantBirthdate = {
        ...applicantBirthdate,
        day: '1',
      };
    }

    return {
      presence: true,
      daterange: {
        requireYear: false,
        earliest: beyondRequired ? requiredDate.datetime : applicantBirthdate,
        latest: DEFAULT_LATEST,
        tooEarly: beyondRequired ? 'error.beforeDuration' : 'error.applicantMin',
      },
    };
  },
  Address: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { presence: true,
        location: {
          requireIsMilitaryDiplomaticAddress: true,
          requireMilitaryDiplomaticInstallationName: true,
          requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
          requireStreet: true,
          validator: pvqLocationModelValidator
}
};
    }
    return {

      presence: true,
      location: {
        militaryAddress: true,
        validator: address,
        allowPOBox: false,
      },
    };
  },

  AlternateAddress: (value, attributes, attributeName, options) => {
    const notSF852013 = options?.formType !== formTypes.SF852013;
    const formDetails = FormDetails.getInstance().getData();

    if (attributes?.Address && isInternational(attributes?.Address) && notSF852013 && !isPVQForm(formDetails.formType)) {
      // Scenario only occurs if incorrect prefill data; HasDifferentAddress maps to 'Did you have an APO/FPO address' here
      // Example: HasDifferentAddress.value = 'Yes' and Address.country = 'Germany'; not a possible scenario in the UI
      if (value?.HasDifferentAddress?.value === 'Yes' && value.Address?.country && !isPO(value.Address)) {
        return {
          presence: true,
          model: {
            validator: physicalAddress,
            onlyMilitaryAddress: true,
            allowPOBox: false,
            hasTelephone: false,
          },
        };
      }

      return {
        presence: true,
        model: {
          validator: physicalAddress,
          militaryAddress: true,
          allowPOBox: false,
          hasTelephone: false,
        },
      };
    }

    if (attributes?.Address && isPO(attributes?.Address) && notSF852013) {
      return {
        presence: true,
        model: {
          validator: physicalAddress,
          militaryAddress: false,
          allowPOBox: false,
          hasTelephone: false,
        },
      };
    }

    return {};
  },

  Role: (value, attributes, attributeName, options) => {
    if (options?.requireHistoryResidenceRole) {
      return {
        presence: true,
        hasValue: { validator: { inclusion: roleOptions } },
      };
    }

    return {};
  },

  RoleOther: (value, attributes, attributeName, options) => {
    if (attributes?.Role?.value === 'Other' && options?.requireHistoryResidenceRole) {
      return { presence: true, hasValue: true };
    }

    return {};
  },

  // Reference required if date range is within 3 years
  ReferenceName: (value, attributes, attributeName, options) => (
    residenceRequiresReference(attributes?.Dates) && options?.requireHistoryResidenceReferenceName
      ? {
        presence: true,
        model: { validator: name },
      }
      : {}),

  ReferenceName2013: (value, attributes, attributeName, options) => (
    residenceRequiresReference(attributes?.Dates) && !options?.requireHistoryResidenceReferenceName
      ? {
        hasValue: true,
      }
      : {}),

  ReferenceLastContact: (value, attributes, attributeName, options) => (
    residenceRequiresReference(attributes?.Dates) && options?.requireHistoryResidenceReferenceInfo
      ? {
        presence: true,
        date: {
          earliest: attributes?.Dates?.from || options?.applicantBirthdate,
          tooEarly: 'error.date.lastContactDateTooEarlyResidence',
          latest: DEFAULT_LATEST,
          requireYear: false,
        },
      }
      : {}),

  ReferencePhoneEvening: (value, attributes, attributeName, options) => (
    residenceRequiresReference(attributes?.Dates) && options?.requireHistoryResidenceReferenceInfo
      ? {
        presence: true,
        model: { validator: phoneModelValidator },
      }
      : {}),

  ReferencePhoneDay: (value, attributes, attributeName, options) => (
    residenceRequiresReference(attributes?.Dates) && options?.requireHistoryResidenceReferenceInfo
      ? {
        presence: true,
        model: { validator: phoneModelValidator },
      }
      : {}),

  ReferencePhoneMobile: (value, attributes, attributeName, options) => (
    residenceRequiresReference(attributes?.Dates) && options?.requireHistoryResidenceReferenceInfo
      ? {
        presence: true,
        model: { validator: phoneModelValidator },
      }
      : {}),

  ReferenceRelationship: (value, attributes, attributeName, options) => (
    residenceRequiresReference(attributes?.Dates) && options?.requireHistoryResidenceReferenceInfo
      ? {
        presence: true,
        array: {
          validator: {
            presence: true,
            inclusion: relationshipOptions,
          },
          length: { minimum: 1, message: REQUIRED },
        },
      }
      : {}),

  ReferenceRelationshipOther: (value, attributes, attributeName, options) => {
    if (attributes?.ReferenceRelationship?.values?.some?.((i) => i === 'Other')
      && options?.requireHistoryResidenceReferenceInfo) {
      return { presence: true, hasValue: true };
    }

    return {};
  },

  ReferenceEmail: (value, attributes, attributeName, options) => {
    const { Dates, ReferenceEmailNotApplicable } = attributes;
    if (options?.requireHistoryResidenceReferenceInfo === false) { return {}; }

    if (!residenceRequiresReference(Dates)) { return {}; }

    if (ReferenceEmailNotApplicable && !ReferenceEmailNotApplicable.applicable) {
      return {};
    }

    return {
      hasValue: { validator: { email: true } },
    };
  },

  ReferenceEmailNotApplicable: {},

  ReferenceAddress: (value, attributes, attributeName, options) => (residenceRequiresReference(attributes?.Dates)
    ? {
      presence: true,
      location: { validator: address },
    }
    : {}),

  ReferenceAlternateAddress: (value, attributes, attributeName, options) => {
    if (residenceRequiresReference(attributes?.Dates)
      && attributes?.ReferenceAddress
      && isInternational(attributes?.ReferenceAddress)
      && options?.requireHistoryResidenceReferenceInfo) {
      return {
        presence: true,
        model: {
          validator: physicalAddress,
          militaryAddress: true,
          onlyMilitaryAddress: true,
          hasTelephone: false,
        },
      };
    }

    if (attributes?.ReferenceAddress
      && isPO(attributes?.ReferenceAddress)
      && options?.requireHistoryResidenceReferenceInfo) {
      return {
        presence: true,
        model: {
          validator: physicalAddress,
          militaryAddress: false,
          hasTelephone: false,
        },
      };
    }

    return {};
  },

  IsTemporaryAddress: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();
    if (!isPVQForm(formDetails.formType)) { return {}; }

    return { presence: true, hasValue: { validator: hasYesOrNo } };
  },

  TemporaryAddressPurpose: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();
    if (!isPVQForm(formDetails.formType) || attributes?.IsTemporaryAddress?.value !== 'Yes') {
      return {};
    }

    return {
      presence: true,
      hasValue: {
        validator: {
          inclusion: temporaryLivingSituationOptions
        }
      }
    };
  },

  TemporaryAddressPurposeExplanation: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();
    if (!isPVQForm(formDetails.formType)
      || attributes?.IsTemporaryAddress?.value !== 'Yes'
      || attributes?.TemporaryAddressPurpose?.value !== 'Other') {
      return {};
    }

    return { presence: true, hasValue: true };
  },
};

export default historyResidenceItemValidator;
